import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, ChevronUp, ChevronDown, FileText, Flag, AlertTriangle, CheckCircle } from 'lucide-react';
import ExportMenu from '../../ExportMenu';
import TimeRangePills, { filterByTimeRange } from '../../TimeRangePills/TimeRangePills';
import PoLink from '../../PoDetailModal/PoLink';
import apiClient from '../../../lib/apiClient';

const FLAG_CATEGORIES = [
  { value: 'wrong_vendor',   label: 'Wrong vendor' },
  { value: 'wrong_category', label: 'Wrong category' },
  { value: 'wrong_product',  label: 'Wrong product / SKU' },
  { value: 'ambiguous',      label: 'Ambiguous source data' },
  { value: 'non_tech',       label: 'Non-tech (shouldn\'t be in pipeline)' },
  { value: 'duplicate',      label: 'Duplicate' },
  { value: 'other',          label: 'Other / unsure' },
];

/**
 * TransactionListModal - Displays transaction details in a modal
 *
 * Shows a sortable table with:
 * - PO # (purchase_document_num)
 * - Date (purchase_order_date)
 * - Item Description
 * - Partner (supplier_name)
 * - Start Date
 * - End Date
 * - Amount (line_total)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close the modal
 * @param {string} props.capabilityCode - The capability code
 * @param {string} props.capabilityName - The capability display name
 * @param {string} props.vendor - Suggested vendor name
 * @param {string} props.product - Suggested product name
 * @param {function} props.fetchTransactions - Function to fetch transaction data
 */
const TransactionListModal = ({
  isOpen,
  onClose,
  capabilityCode,
  capabilityName,
  vendor,
  product,
  fetchTransactions
}) => {
  const [transactions, setTransactions] = useState([]);
  // Large multi-year EAs outside the active window. Softer signal — shown
  // as a separate section below the primary table. See zeroTrustService.getCapabilityTransactions.
  const [recommendations, setRecommendations] = useState([]);
  // Matched rows that don't meet active or EA thresholds. Third section so
  // reviewers still see what the AI matched against even when nothing
  // qualifies as "current coverage."
  const [historical, setHistorical] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalSpend, setTotalSpend] = useState(0);
  const [recommendationsTotalSpend, setRecommendationsTotalSpend] = useState(0);
  const [historicalTotalSpend, setHistoricalTotalSpend] = useState(0);
  // Backend debug info when the result is empty — tells us whether the dept
  // has any rows, whether the vendor pattern matched anything, etc.
  const [debug, setDebug] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'purchaseOrderDate', direction: 'desc' });
  const [timeRange, setTimeRange] = useState('all');
  const [timeRangeCutoff, setTimeRangeCutoff] = useState(null);

  // Flag-as-suspicious state. One row at a time — same UX as ClassificationUnified's
  // flag panel. Flags land in classification_flags and appear in the Review Queue.
  const [flaggingId, setFlaggingId] = useState(null);
  const [flagCategory, setFlagCategory] = useState('wrong_vendor');
  const [flagReason, setFlagReason] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagError, setFlagError] = useState(null);
  // item_id → count of open flags. Seeded from flags already on rows, incremented on submit.
  const [openFlagsById, setOpenFlagsById] = useState({});

  useEffect(() => {
    if (isOpen && capabilityCode && fetchTransactions) {
      loadTransactions();
    }
  }, [isOpen, capabilityCode]);

  const loadTransactions = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchTransactions(capabilityCode);
      setTransactions(data.transactions || []);
      setTotalSpend(data.totalSpend || 0);
      setRecommendations(data.recommendations || []);
      setRecommendationsTotalSpend(data.recommendationsTotalSpend || 0);
      setHistorical(data.historical || []);
      setHistoricalTotalSpend(data.historicalTotalSpend || 0);
      setDebug(data.debug || null);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const openFlag = (row) => {
    setFlaggingId(row.id);
    setFlagCategory('wrong_vendor');
    setFlagReason('');
    setFlagError(null);
  };

  const closeFlag = () => {
    setFlaggingId(null);
    setFlagReason('');
    setFlagError(null);
  };

  const submitFlag = async () => {
    if (flagReason.trim().length < 3 || flaggingId == null) return;
    setFlagSubmitting(true);
    setFlagError(null);
    try {
      const resp = await apiClient.post('/api/classification/flags', {
        item_id: flaggingId,
        flag_reason: flagReason.trim(),
        flag_category: flagCategory,
      });
      setOpenFlagsById(prev => ({
        ...prev,
        [flaggingId]: resp?.open_flag_count ?? ((prev[flaggingId] || 0) + 1),
      }));
      closeFlag();
    } catch (e) {
      setFlagError(e?.response?.data?.error || e?.message || 'Failed to submit flag');
    } finally {
      setFlagSubmitting(false);
    }
  };

  // Shared filter + sort, applied to both the active transactions and the
  // EA recommendations list so they feel like one coherent table pair.
  const sortByConfig = useMemo(() => (list) => {
    if (!list.length) return [];
    return [...list].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      let comparison = 0;

      if (aVal == null && bVal == null) {
        comparison = 0;
      } else if (aVal == null) {
        comparison = 1;
      } else if (bVal == null) {
        comparison = -1;
      } else if (sortConfig.key.includes('Date')) {
        const aDate = new Date(aVal);
        const bDate = new Date(bVal);
        comparison = sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      } else if (sortConfig.key === 'lineTotal') {
        comparison = sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
        if (sortConfig.direction === 'desc') comparison = -comparison;
      }

      if (comparison === 0) {
        const poA = a.purchaseDocumentNum || '';
        const poB = b.purchaseDocumentNum || '';
        comparison = poA.localeCompare(poB);
      }
      if (comparison === 0) {
        const lineA = parseInt(a.purchaseDocumentLine, 10) || 0;
        const lineB = parseInt(b.purchaseDocumentLine, 10) || 0;
        comparison = lineA - lineB;
      }

      return comparison;
    });
  }, [sortConfig]);

  const filteredTransactions = useMemo(() => {
    return filterByTimeRange(transactions, 'purchaseOrderDate', timeRangeCutoff);
  }, [transactions, timeRangeCutoff]);

  const filteredRecommendations = useMemo(() => {
    return filterByTimeRange(recommendations, 'purchaseOrderDate', timeRangeCutoff);
  }, [recommendations, timeRangeCutoff]);

  const filteredHistorical = useMemo(() => {
    return filterByTimeRange(historical, 'purchaseOrderDate', timeRangeCutoff);
  }, [historical, timeRangeCutoff]);

  const filteredTotalSpend = useMemo(() => {
    if (filteredTransactions.length === transactions.length) return totalSpend;
    return filteredTransactions.reduce((sum, t) => sum + (t.lineTotal || 0), 0);
  }, [filteredTransactions, transactions.length, totalSpend]);

  const filteredRecommendationsSpend = useMemo(() => {
    if (filteredRecommendations.length === recommendations.length) return recommendationsTotalSpend;
    return filteredRecommendations.reduce((sum, t) => sum + (t.lineTotal || 0), 0);
  }, [filteredRecommendations, recommendations.length, recommendationsTotalSpend]);

  const filteredHistoricalSpend = useMemo(() => {
    if (filteredHistorical.length === historical.length) return historicalTotalSpend;
    return filteredHistorical.reduce((sum, t) => sum + (t.lineTotal || 0), 0);
  }, [filteredHistorical, historical.length, historicalTotalSpend]);

  const sortedTransactions = useMemo(() => sortByConfig(filteredTransactions), [filteredTransactions, sortByConfig]);
  const sortedRecommendations = useMemo(() => sortByConfig(filteredRecommendations), [filteredRecommendations, sortByConfig]);
  const sortedHistorical = useMemo(() => sortByConfig(filteredHistorical), [filteredHistorical, sortByConfig]);

  // Historical is shown collapsed by default when active/recs have rows,
  // expanded by default when they don't (so the user always sees something).
  const historicalDefaultOpen = filteredTransactions.length === 0 && filteredRecommendations.length === 0;
  const [historicalOpen, setHistoricalOpen] = useState(historicalDefaultOpen);
  useEffect(() => {
    setHistoricalOpen(historicalDefaultOpen);
  }, [historicalDefaultOpen]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const truncateText = (text, maxLength = 60) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Source data has POs like "0000000000000000000077887" — pure leading-zero
  // padding that crowds out the Amount column when rendered. Strip the zeros
  // for display; export keeps the full value.
  const formatPo = (po) => {
    if (!po) return '-';
    const s = String(po);
    return s.replace(/^0+(?=\d)/, '');
  };

  // Column definitions for export
  const exportColumns = [
    { key: 'purchaseDocumentNum', label: 'PO #' },
    { key: 'purchaseOrderDate', label: 'Date' },
    { key: 'itemDescription', label: 'Item Description' },
    { key: 'supplierName', label: 'Partner' },
    { key: 'startDate', label: 'Start Date' },
    { key: 'endDate', label: 'End Date' },
    { key: 'lineTotal', label: 'Amount' }
  ];

  const SortHeader = ({ label, sortKey }) => (
    <th
      className="transaction-th sortable"
      onClick={() => handleSort(sortKey)}
    >
      <span className="transaction-th-content">
        {label}
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc'
            ? <ChevronUp size={12} />
            : <ChevronDown size={12} />
        )}
      </span>
    </th>
  );

  // Shared table renderer — used for both the Active section and the
  // EA Recommendations section. Declared before `return` so the const
  // binding is available when the JSX below references it.
  const renderTable = (rows, accent) => (
    <div className={`transaction-table-wrapper transaction-table-wrapper--${accent}`}>
      <table className="transaction-table">
        {/* Fixed colgroup keeps every column at a predictable width, which
            is what makes <thead> line up with <tbody> under table-layout:
            fixed. Widths set inline (not via class) so no CSS cascade can
            override them — this used to drift when the .transaction-th
            sticky rule re-ran the layout pass on the header alone. */}
        <colgroup>
          <col style={{ width: '70px' }} />
          <col style={{ width: '110px' }} />
          <col />
          <col style={{ width: '170px' }} />
          <col style={{ width: '110px' }} />
          <col style={{ width: '110px' }} />
          <col style={{ width: '110px' }} />
          <col style={{ width: '44px' }} />
        </colgroup>
        <thead>
          <tr>
            <SortHeader label="PO #" sortKey="purchaseDocumentNum" />
            <SortHeader label="Date" sortKey="purchaseOrderDate" />
            <th className="transaction-th">Item Description</th>
            <SortHeader label="Partner" sortKey="supplierName" />
            <SortHeader label="Start" sortKey="startDate" />
            <SortHeader label="End" sortKey="endDate" />
            <SortHeader label="Amount" sortKey="lineTotal" />
            <th className="transaction-th transaction-th-flag" title="Flag as suspicious">
              <Flag size={12} />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, idx) => {
            const openFlags = openFlagsById[t.id] || 0;
            const isFlagging = flaggingId === t.id;
            return (
              <React.Fragment key={t.id || idx}>
                <tr className={`transaction-row ${openFlags > 0 ? 'transaction-row--flagged' : ''}`}>
                  <td className="transaction-td transaction-td-po" title={t.purchaseDocumentNum || undefined}>
                    {t.purchaseDocumentNum
                      ? <PoLink documentNumber={t.purchaseDocumentNum} departmentId={t.departmentId}>{formatPo(t.purchaseDocumentNum)}</PoLink>
                      : formatPo(t.purchaseDocumentNum)}
                  </td>
                  <td className="transaction-td transaction-td-date">
                    {formatDate(t.purchaseOrderDate)}
                  </td>
                  <td
                    className="transaction-td transaction-td-desc"
                    title={t.itemDescription}
                  >
                    {truncateText(t.itemDescription)}
                  </td>
                  <td className="transaction-td transaction-td-partner">
                    {truncateText(t.supplierName, 30)}
                  </td>
                  <td className="transaction-td transaction-td-date">
                    {formatDate(t.startDate)}
                  </td>
                  <td className="transaction-td transaction-td-date">
                    {formatDate(t.endDate)}
                  </td>
                  <td className="transaction-td transaction-td-amount">
                    {formatCurrency(t.lineTotal)}
                  </td>
                  <td className="transaction-td transaction-td-flag">
                    <button
                      type="button"
                      className={`transaction-flag-btn ${openFlags > 0 ? 'transaction-flag-btn--flagged' : ''} ${isFlagging ? 'transaction-flag-btn--active' : ''}`}
                      onClick={() => (isFlagging ? closeFlag() : openFlag(t))}
                      title={
                        openFlags > 0
                          ? `${openFlags} open flag(s) — click to add another`
                          : 'Flag this row as suspicious'
                      }
                      disabled={!t.id}
                    >
                      <Flag size={12} />
                      {openFlags > 0 && <span className="transaction-flag-count">{openFlags}</span>}
                    </button>
                  </td>
                </tr>
                {isFlagging && (
                  <tr className="transaction-row-flag-form">
                    <td colSpan={8}>
                      <div className="transaction-flag-panel">
                        <div className="transaction-flag-panel-header">
                          <AlertTriangle size={14} />
                          <strong>Flag as suspicious</strong>
                          <span className="transaction-flag-panel-hint">
                            Lands in the classification review queue. No right answer required.
                          </span>
                          <button
                            type="button"
                            className="transaction-flag-panel-close"
                            onClick={closeFlag}
                            aria-label="Cancel flag"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <div className="transaction-flag-panel-body">
                          <select
                            className="transaction-flag-select"
                            value={flagCategory}
                            onChange={(e) => setFlagCategory(e.target.value)}
                            disabled={flagSubmitting}
                          >
                            {FLAG_CATEGORIES.map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                          <textarea
                            className="transaction-flag-textarea"
                            value={flagReason}
                            onChange={(e) => setFlagReason(e.target.value)}
                            placeholder="What looks wrong? (required, min 3 chars)"
                            rows={2}
                            maxLength={1000}
                            disabled={flagSubmitting}
                          />
                        </div>
                        {flagError && (
                          <div className="transaction-flag-panel-error">
                            {flagError}
                          </div>
                        )}
                        <div className="transaction-flag-panel-actions">
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={closeFlag}
                            disabled={flagSubmitting}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn-primary btn-sm"
                            onClick={submitFlag}
                            disabled={flagSubmitting || flagReason.trim().length < 3}
                          >
                            {flagSubmitting
                              ? <><Loader2 size={12} className="spin" /> Submitting…</>
                              : <><CheckCircle size={12} /> Submit flag</>}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="transaction-modal-overlay" onClick={onClose}>
      <div className="transaction-modal" onClick={e => e.stopPropagation()}>
        <div className="transaction-modal-header">
          <div className="transaction-modal-title">
            <FileText size={18} />
            <div className="transaction-modal-title-text">
              <h3>{capabilityName || capabilityCode}</h3>
              {vendor && (
                <span className="transaction-modal-vendor">
                  {vendor}{product ? ` - ${product}` : ''}
                </span>
              )}
            </div>
          </div>
          <button
            className="transaction-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        </div>

        <div className="transaction-modal-summary">
          <span className="transaction-count">
            {filteredTransactions.length} active transaction{filteredTransactions.length !== 1 ? 's' : ''}
            {filteredRecommendations.length > 0 && (
              <span className="transaction-count-subtle">
                {' · '}{filteredRecommendations.length} EA recommendation{filteredRecommendations.length !== 1 ? 's' : ''}
              </span>
            )}
            {filteredHistorical.length > 0 && (
              <span className="transaction-count-subtle">
                {' · '}{filteredHistorical.length} historical
              </span>
            )}
          </span>
          <span className="transaction-total">
            Active: {formatCurrency(filteredTotalSpend)}
            {filteredRecommendationsSpend > 0 && (
              <span className="transaction-total-subtle">
                {' · '}EA: {formatCurrency(filteredRecommendationsSpend)}
              </span>
            )}
            {filteredHistoricalSpend > 0 && (
              <span className="transaction-total-subtle">
                {' · '}Hist: {formatCurrency(filteredHistoricalSpend)}
              </span>
            )}
          </span>
          <TimeRangePills
            selected={timeRange}
            onChange={(key, cutoff) => { setTimeRange(key); setTimeRangeCutoff(cutoff); }}
          />
          <ExportMenu
            data={[...sortedTransactions, ...sortedRecommendations, ...sortedHistorical]}
            columns={exportColumns}
            filename={`${capabilityCode || 'capability'}-transactions`}
            size="sm"
            disabled={loading || (filteredTransactions.length === 0 && filteredRecommendations.length === 0 && filteredHistorical.length === 0)}
          />
        </div>

        {/* ALWAYS-ON diagnostic banner. Shows even when `debug` is missing
            so we can distinguish "backend deploy stuck (no debug field)"
            from "backend returned data successfully". */}
        {!loading && (
          <div className={`transaction-debug-banner ${
            !debug ? 'transaction-debug-banner--missing'
            : debug.rowsReturned === 0 ? 'transaction-debug-banner--empty'
            : ''
          }`}>
            {!debug ? (
              <>
                <strong>⚠️ Backend deploy lag:</strong> the API responded but didn't
                include a <code>debug</code> block. This means Render hasn't picked up
                the latest backend commit yet (commit 378246fd or later).
              </>
            ) : (
              <>
                <strong>Backend says:</strong>{' '}
                {debug.totalDeptRows} total dept rows ·{' '}
                {debug.vendorMatchedRows} vendor-matched ·{' '}
                {debug.rowsReturned} returned
                {debug.tierBreakdown && (
                  <> ({debug.tierBreakdown.active} active / {debug.tierBreakdown.ea_recommendation} ea / {debug.tierBreakdown.historical} historical)</>
                )}
                {debug.matchedVendorNames?.length > 0 && (
                  <div className="transaction-debug-banner-sub">
                    Matched vendor names: <code>{debug.matchedVendorNames.join(', ')}</code>
                  </div>
                )}
                {debug.vendorPatterns && (
                  <div className="transaction-debug-banner-sub">
                    Vendor patterns: <code>{debug.vendorPatterns.join(', ')}</code>
                  </div>
                )}
                {debug.totalDeptRows > 0 && debug.vendorMatchedRows === 0 && (
                  <div className="transaction-debug-banner-hint">
                    ⚠️ Department has data but none of this capability's vendor patterns matched.
                    Share this banner + the suggestion text so we can reconcile.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="transaction-modal-content">
          {loading ? (
            <div className="transaction-loading">
              <Loader2 size={24} className="spin" />
              <p>Loading transactions...</p>
            </div>
          ) : error ? (
            <div className="transaction-error">
              <p>{error}</p>
              <button className="btn-secondary btn-sm" onClick={loadTransactions}>
                Retry
              </button>
            </div>
          ) : (filteredTransactions.length === 0 && filteredRecommendations.length === 0 && filteredHistorical.length === 0) ? (
            <div className="transaction-empty">
              <p>{timeRange !== 'all' ? 'No transactions in this time range. Try a wider range.' : 'No matching transactions found for this capability.'}</p>
            </div>
          ) : (
            <>
              {/* Active / recent contracts — primary signal */}
              {filteredTransactions.length > 0 && (
                <div className="transaction-section transaction-section--active">
                  <div className="transaction-section-header">
                    <h4>Active contracts &amp; recent purchases</h4>
                    <span className="transaction-section-sub">
                      Contracts with an end date in the future or purchases in the last 2 years.
                    </span>
                  </div>
                  {renderTable(sortedTransactions, 'active')}
                </div>
              )}

              {/* Multi-year EAs outside the active window — softer signal */}
              {filteredRecommendations.length > 0 && (
                <div className="transaction-section transaction-section--recommendations">
                  <div className="transaction-section-header">
                    <h4>Multi-year EAs that may still cover</h4>
                    <span className="transaction-section-sub">
                      Large-ticket items (≥ $250k) spanning ≥ 2 years or flagged as Enterprise/Master Agreements. These
                      are recommendations — not confirmed active coverage.
                    </span>
                  </div>
                  {renderTable(sortedRecommendations, 'recommendations')}
                </div>
              )}

              {/* Historical — rows the AI matched that don't meet active/EA
                  thresholds. Collapsed when we have active/recs; expanded
                  otherwise so the user always sees what matched. */}
              {filteredHistorical.length > 0 && (
                <div className="transaction-section transaction-section--historical">
                  <button
                    type="button"
                    className="transaction-section-header transaction-section-header--toggle"
                    onClick={() => setHistoricalOpen(o => !o)}
                    aria-expanded={historicalOpen}
                  >
                    <span>
                      <h4>Older procurement history ({filteredHistorical.length})</h4>
                      <span className="transaction-section-sub">
                        Rows the AI matched that fall outside the active window and the EA threshold. Shown for
                        context — do not assume these still cover the capability.
                      </span>
                    </span>
                    {historicalOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {historicalOpen && renderTable(sortedHistorical, 'historical')}
                </div>
              )}
            </>
          )}
        </div>

        <div className="transaction-modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

};

export default TransactionListModal;
